import { TiptapEditorView } from '@amzn/awscat-editor';
import {
  Box,
  Container,
  Grid,
  SpaceBetween,
  Button,
  ColumnLayout,
} from '@amzn/awsui-components-core';
import {
  Content,
  ContentSubType,
  ContentType,
  ContentWorkflow,
  Publication,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useContext, useEffect, useState } from 'react';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import DeleteContentButton from '../../content/DeleteContentButton';
import { FeedbackRatingSystem, FeedbackRating } from '../../feedback-service';
import ContentFeedback from '../../feedback-service/ContentFeedback';
import UnpublishButton from '../../publication/UnpublishButton';
import BrowserHistory from '../../utils/BrowserHistory';
import ErrorBoundary from '../../../editor/ErrorBoundary';
import { contentSubTypeDisplayNames } from '../../utils/DisplayNames';
import ReleaseNoteList from './ReleaseNoteList';
import { AppContext } from '../../../contexts/AppContext';

interface Props {
  postId: string;
  post: Content;
  workflow?: ContentWorkflow;
  allPosts?: Publication[];
  showEditingControl?: boolean;
  onEditSubmited?: () => Promise<void>;
  fetchImage: (srcUrl: string) => Promise<string>;
}

const PostReadonlyViewer = (props: Props) => {
  const { post, workflow, showEditingControl, onEditSubmited, fetchImage } = props;
  const appContext = useContext(AppContext);
  const { entities } = appContext;
  const [isFetchingPosts, setIsFetchingPosts] = useState(false);
  const [allReleases, setAllReleases] = useState(props?.allPosts || []);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsFetchingPosts(true);
      try {
        const mplService = MplServiceClientFactory.create();
        const { publications } = await mplService.listPublicationsThroughS3();
        const releaseNotes = publications.filter(
          (p) =>
            p.contentMetadata.contentType === ContentType.Post &&
            p.contentTypeMetadata.contentSubType === ContentSubType.ReleaseNotes,
        );
        setAllReleases(releaseNotes);
      } finally {
        setIsFetchingPosts(false);
      }
    };

    if (!props.allPosts) {
      fetchPosts();
    }
  }, [props.allPosts]);
  const editControl = (
    <div className="awsui-cat-flex float-right">
      <ModalDialogButtonV3
        variant="icon"
        iconName="edit"
        header={'Edit post'}
        text={'Do you want to edit this post?'}
        onSubmit={onEditSubmited}
      />
      {workflow && (
        <DeleteContentButton
          workflow={workflow}
          variant="icon"
          iconUrl={'/images/icon-delete.svg'}
          iconAlign="right"
          onDeleteContent={() => BrowserHistory.push('/whats-new')}
        />
      )}
    </div>
  );

  const postContent = (
    <>
      <Container>
        <Box display="inline-block" color="text-body-secondary">
          <Box variant="strong">Posted</Box> {workflow?.publicationDate?.toISODate()}{' '}
          <span>&middot;</span>{' '}
          <Box variant="strong">
            {contentSubTypeDisplayNames.get(post?.contentTypeMetadata?.contentSubType)}
          </Box>
        </Box>
        {showEditingControl ? editControl : null}
        <Box variant="h2">{post?.contentMetadata?.contentName}</Box>
        <ErrorBoundary
          fallback={
            <TiptapEditorView
              model={post?.contentData?.contentSections[0]?.contentData}
              // @ts-expect-error TS2322 - strict migration
              entities={entities}
              fetchImage={fetchImage}
            />
          }
        >
          <TiptapEditorView
            model={post?.contentData?.contentSections[0]?.contentData}
            // @ts-expect-error TS2322 - strict migration
            entities={entities}
            fetchImage={fetchImage}
          />
        </ErrorBoundary>

        <Button
          iconName="arrow-left"
          variant="link"
          onClick={() => BrowserHistory.push('/whats-new')}
        >
          Return to all posts
        </Button>
      </Container>
      {workflow?.publicationState && workflow?.publicationState !== WorkflowState.UNPUBLISHED && (
        <Box float="right">
          <UnpublishButton contentId={workflow?.contentId} />
        </Box>
      )}
    </>
  );

  return (
    <SpaceBetween direction="vertical" size="s">
      <Box variant="h2">What's New</Box>
      <hr />
      <Grid className="create-post-form" gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
        <div>{postContent}</div>
        <ReleaseNoteList isLoading={isFetchingPosts} releaseNotes={allReleases} />
      </Grid>
      <Box>Was this information helpful for you?</Box>
      <ContentFeedback
        contentId={props.postId}
        ratingSystem={FeedbackRatingSystem.LikeDislike}
        requestWrittenFeedback={false}
      />
    </SpaceBetween>
  );
};

export default PostReadonlyViewer;
